import { LanguageCodes } from "../types";

// Los labels del framework se escriben en sus tres idiomas; el proyecto puede
// configurar un subconjunto (LanguageCodes) y el lookup solo usa esos.
type Translations = Record<LanguageCodes, string> &
  Partial<Record<"es" | "en" | "pt", string>>;

/**
 * Un texto que nombra un ícono por su tooltip, que es del LangStore generado:
 * si el generador cambia el tooltip, el texto lo sigue. `t` arma el texto de
 * cada idioma con el nombre del ícono en ese idioma.
 */
function conIcono(
  icono: "emailValidateData" | "emailResendValidateData",
  t: Record<LanguageCodes, (nombre: string) => string>
): PropertyDescriptor {
  return {
    get(this: { language: LanguageCodes } & Record<typeof icono, string>) {
      return t[this.language](this[icono]);
    },
    enumerable: true,
    configurable: true,
  };
}

// Exportado: es la API del punto de extensión; si no hay labels custom aún,
// el export evita el error de noUnusedLocals en proyectos vírgenes.
export function langGetter(t: Translations): PropertyDescriptor {
  return {
    get(this: { language: LanguageCodes }) {
      return t[this.language];
    },
    enumerable: true,
    configurable: true,
  };
}

declare module "./LangStore" {
  interface LangStore {
    // Edition custom labels
    editionDocumentationReadyTitle: string;
    editionDocumentationReadyMessage: string;
    editionDocumentationFailedTitle: string;
    editionDocumentationFailedMessage: string;
    editionEjemplarReadyTitle: string;
    editionEjemplarReadyMessage: string;
    editionTypeSingleWorkRequired: string;
    editionTypeSingleWorkNotAllowed: string;
    editionWorkflowCreateHint: string;
    // Label del card manual "Estado de la Edición" (el grupo editionWorkflow ya no
    // se genera en haiku; vive acá para sobrevivir regeneraciones).
    groupEditionEditionWorkflowLabel: string;
    downloadAllFilesLabel: string;
    signedPdfViewerWarning: string;
    // CUIT/CUIL check-digit validation (Publisher / TenantProfile)
    cuitCheckDigitInvalid: string;
    // TadManagerConfig: coherencia proveedor de identidad TAD <-> documento/credencial del gestor
    tadManagerAuthProviderRequiresCuit: string;
    tadManagerAuthProviderRequiresCredential: string;
    tadManagerAuthProviderCredentialLoaded: string;
    // Publisher (natural person) custom validation labels
    publisherNaturalPersonMaritalStatusRequired: string;
    publisherNaturalPersonNationalityRequired: string;
    publisherRequiresSignerRepresentative: string;
    // AuthorComposer custom validation labels
    authorPublisherSameCuit: string;
    // PaymentReceipt custom validation labels
    paymentReceiptAmountBelowPrice: string;
    authorFrozenByAnsesCert: string;
    // WorkAuthor custom validation labels
    workAuthorPercentagesBothZero: string;
    // ContractObservation custom labels
    contractObservationIncludeInAllLabel: string;
    tenantSetupTitle: string;
    tenantSetupIntro: string;
    tenantSetupCreatePublisher: string;
    tenantSetupEditProfile: string;
    tenantSetupItemOwnPublisher: string;
    tenantSetupItemSigner: string;
    tenantSetupItemFtp: string;
    tenantSetupItemTad: string;
    // Revisión de campos del contrato (visor de la edición firmada)
    contractFieldsReviewButton: string;
    contractFieldsDialogTitle: string;
    contractFieldsHint: string;
    contractFieldsNoMapNotice: string;
    contractFieldsLoadError: string;
    contractFieldsReloadPage: string;
    contractFieldsReloadButton: string;
    contractFieldsOccurrenceOf: string;
    contractFieldsGroupPublishers: string;
    contractFieldsGroupAuthors: string;
    contractFieldsGroupWorks: string;
    contractFieldsGroupTerritories: string;
    contractFieldsGroupConditions: string;
    contractFieldsGroupEditorial: string;
    contractFieldsGroupEdition: string;
    contractFieldsGroupSigners: string;
    contractFieldsGroupDerived: string;
    contractFieldsGroupOther: string;
    // KAN-448: la letra no coincide con los autores de letra (un renglón por
    // obra; {title} es la obra y {field}, la etiqueta del campo de la letra)
    editionLyricsWithoutLyricists: string;
    editionLyricistsWithoutLyrics: string;
    // KAN-450: autores con datos faltantes al generar la documentación
    // ({author} es el autor; {fields}, las etiquetas de los datos que faltan)
    editionAuthorMissingOne: string;
    editionAuthorMissingMany: string;
    // KAN-450: datos validados con DNI y advertencias del autor
    authorDniValidatedField: string;
    authorDniValidatedRecord: string;
    authorIncompleteWarning: string;
    authorWithoutIpiOrDniWarning: string;
    authorStatusColumn: string;
    // KAN-450: ofrecer la solicitud de validación al guardar el alta
    // ({name} y {email} son los del autor recién dado de alta)
    authorValidationOfferTitle: string;
    authorValidationOfferBody: string;
    authorValidationOfferSend: string;
    authorValidationOfferLater: string;
    authorValidationOfferSent: string;
    authorValidationOfferAlreadyValidated: string;
    authorValidationOfferAlreadyVerified: string;
    authorValidationOfferRateLimited: string;
    authorValidationOfferFailed: string;
    authorValidationOfferNoResponse: string;
  }
}

export function customLabelDescriptors(): PropertyDescriptorMap {
  return {
    /**
     * ── Ediciones de SOLO audios: ocultas, no eliminadas ──
     *
     * Por ahora no se dan de alta ediciones de solo audios. Se oculta la opción
     * del selector en lugar de sacar el valor del enum, porque TODO el circuito
     * —validaciones, ejemplar, DNDA, SADAIC— la sigue soportando y las
     * ediciones ya firmadas la tienen en su snapshot congelado.
     *
     * Va acá y no en el store generado porque este archivo es del proyecto: el
     * merge base→develop lo conserva y la regeneración no lo pisa.
     *
     * PARA VOLVER A HABILITARLA: borrar este descriptor. Nada más — el resto
     * del código nunca dejó de contemplar el caso.
     *
     * Hoy no hay ninguna edición viva con este valor (verificado en base), así
     * que ningún formulario queda sin poder mostrar el suyo. Si apareciera una,
     * el selector se vería vacío al editarla, pero el valor NO se pierde: se
     * guarda lo que ya tenía mientras nadie lo toque.
     */
    EditionContentTypeOptions: {
      get(this: {
        EditionContentTypeScoresLabel: string;
        EditionContentTypeScoresAndAudiosLabel: string;
      }) {
        return [
          { id: "scores", displayId: this.EditionContentTypeScoresLabel },
          {
            id: "scoresAndAudios",
            displayId: this.EditionContentTypeScoresAndAudiosLabel,
          },
        ];
      },
      enumerable: true,
      configurable: true,
    },

    // ── Edition custom labels ──
    editionDocumentationReadyTitle: langGetter({
      es: "Documentación lista",
      en: "Documentation ready",
      pt: "Documentação pronta",
    }),
    editionDocumentationReadyMessage: langGetter({
      es: "La documentación de la Edición {name} está lista",
      en: "Documentation for Edition {name} is ready",
      pt: "A documentação da Edição {name} está pronta",
    }),
    editionDocumentationFailedTitle: langGetter({
      es: "Error en documentación",
      en: "Documentation error",
      pt: "Erro na documentação",
    }),
    editionDocumentationFailedMessage: langGetter({
      es: "Error generando documentación para {name}",
      en: "Error generating documentation for {name}",
      pt: "Erro gerando documentação para {name}",
    }),
    editionEjemplarReadyTitle: langGetter({
      es: "Ejemplar de edición listo",
      en: "Edition copy ready",
      pt: "Exemplar de edição pronto",
    }),
    editionEjemplarReadyMessage: langGetter({
      es: "El ejemplar de edición de {name} está listo para descargar",
      en: "Edition copy for {name} is ready to download",
      pt: "O exemplar de edição de {name} está pronto para download",
    }),
    editionTypeSingleWorkRequired: langGetter({
      es: "Para una sola obra, el tipo debe ser 'Obra suelta'",
      en: "For a single work, the type must be 'Single Work'",
      pt: "Para uma única obra, o tipo deve ser 'Obra avulsa'",
    }),
    editionTypeSingleWorkNotAllowed: langGetter({
      es: "Para más de una obra, el tipo no puede ser 'Obra suelta'",
      en: "For multiple works, the type cannot be 'Single Work'",
      pt: "Para mais de uma obra, o tipo não pode ser 'Obra avulsa'",
    }),
    editionWorkflowCreateHint: langGetter({
      es: "Para poder generar la documentación es necesario primero completar todos los datos requeridos y luego \"Guardar\". Al guardar, el servidor valida los datos e inicia el workflow de la edición, habilitando recién entonces la generación de la documentación, su firma, y el registro en la DNDA y en SADAIC.",
      en: "To generate documentation you must first fill in all required data and then \"Save\". Saving triggers server-side validation and initializes the edition workflow, which then enables documentation generation, signing, and registration with DNDA and SADAIC.",
      pt: "Para gerar a documentação é necessário primeiro preencher todos os dados requeridos e então \"Salvar\". Ao salvar, o servidor valida os dados e inicia o workflow da edição, habilitando então a geração da documentação, sua assinatura e o registro na DNDA e na SADAIC.",
    }),
    groupEditionEditionWorkflowLabel: langGetter({
      es: "Estado de la Edición",
      en: "Edition Status",
      pt: "Estado da Edição",
    }),
    downloadAllFilesLabel: langGetter({
      es: "Descargar todos los archivos",
      en: "Download all files",
      pt: "Baixar todos os arquivos",
    }),
    signedPdfViewerWarning: langGetter({
      es: "Este PDF es solo de muestra. El documento con validez legal y firma digital certificada se encuentra dentro del archivo ZIP descargable.",
      en: "This PDF is for preview only. The legally valid digitally signed document is inside the downloadable ZIP file.",
      pt: "Este PDF é apenas para visualização. O documento com validade legal e assinatura digital certificada está dentro do arquivo ZIP para download.",
    }),
    // ── CUIT/CUIL check-digit validation ──
    cuitCheckDigitInvalid: langGetter({
      es: "CUIT/CUIL inválido: el dígito verificador no corresponde",
      en: "Invalid CUIT/CUIL: the check digit does not match",
      pt: "CUIT/CUIL inválido: o dígito verificador não confere",
    }),
    tadManagerAuthProviderRequiresCuit: langGetter({
      es: "El Proveedor de Identidad elegido exige identificar al gestor con CUIT/CUIL, no con DNI",
      en: "The selected Identity Provider requires identifying the manager with a Tax ID (CUIT/CUIL), not a National ID",
      pt: "O Provedor de Identidade selecionado exige identificar o gestor com CUIT/CUIL, não com DNI",
    }),
    tadManagerAuthProviderRequiresCredential: langGetter({
      es: "El Proveedor de Identidad elegido requiere cargar esta credencial",
      en: "The selected Identity Provider requires this credential to be filled in",
      pt: "O Provedor de Identidade selecionado requer o preenchimento desta credencial",
    }),
    tadManagerAuthProviderCredentialLoaded: langGetter({
      es: "Credencial cargada",
      en: "Credential loaded",
      pt: "Credencial carregada",
    }),
    // ── Publisher (natural person) custom validation labels ──
    publisherNaturalPersonMaritalStatusRequired: langGetter({
      es: "Estado civil requerido para Editorial Persona Física",
      en: "Marital status required for Natural Person Publisher",
      pt: "Estado civil obrigatório para Editora Pessoa Física",
    }),
    publisherNaturalPersonNationalityRequired: langGetter({
      es: "Nacionalidad requerida para Editorial Persona Física",
      en: "Nationality required for Natural Person Publisher",
      pt: "Nacionalidade obrigatória para Editora Pessoa Física",
    }),
    publisherRequiresSignerRepresentative: langGetter({
      es: "La editorial debe tener al menos un representante firmante",
      en: "The publisher must have at least one signing representative",
      pt: "A editora deve ter ao menos um representante signatário",
    }),
    // ── PaymentReceipt custom validation labels ──
    paymentReceiptAmountBelowPrice: langGetter({
      es: "El importe debe cubrir al menos un uso: no llega al precio vigente del tipo de trámite.",
      en: "The amount must cover at least one use: it does not reach the current price of the process type.",
      pt: "O valor deve cobrir pelo menos um uso: não chega ao preço vigente do tipo de trâmite.",
    }),
    // ── AuthorComposer custom validation labels ──
    authorPublisherSameCuit: langGetter({
      es: "No se puede asociar una editorial persona física con el mismo CUIT/CUIL que el autor.",
      en: "Cannot associate a natural-person publisher with the same Tax ID as the author.",
      pt: "Não é possível associar uma editora pessoa física com o mesmo CUIT/CUIL que o autor.",
    }),
    authorFrozenByAnsesCert: langGetter({
      es: "Los campos Nombre, Apellido, CUIT/CUIL, Género y Nacionalidad ya están validados en ANSES y no pueden modificarse.",
      en: "The Name, Surname, CUIT/CUIL, Gender and Nationality fields are already validated by ANSES and cannot be modified.",
      pt: "Os campos Nome, Sobrenome, CUIT/CUIL, Gênero e Nacionalidade já estão validados no ANSES e não podem ser modificados.",
    }),
    // ── WorkAuthor custom validation labels ──
    workAuthorPercentagesBothZero: langGetter({
      es: "El autor debe participar en letra o en música: no puede tener 0% en ambas",
      en: "The author must participate in lyrics or music: both cannot be 0%",
      pt: "O autor deve participar na letra ou na música: ambas não podem ser 0%",
    }),
    // ── ContractObservation custom labels ──
    contractObservationIncludeInAllLabel: langGetter({
      es: "Incluir en todos los contratos de edición musical",
      en: "Include in every musical edition contract",
      pt: "Incluir em todos os contratos de edição musical",
    }),
    // ── Guía de configuración inicial del tenant (setup del circuito) ──
    tenantSetupTitle: langGetter({
      es: "Falta configurar la editorial para empezar a operar",
      en: "Publisher setup is needed to start operating",
      pt: "Falta configurar a editora para começar a operar",
    }),
    tenantSetupIntro: langGetter({
      es: "Para crear ediciones, generar documentación, firmar, subir a SADAIC y tramitar en DNDA, primero hace falta:",
      en: "To create editions, generate documentation, sign, upload to SADAIC and file with DNDA, the following is needed first:",
      pt: "Para criar edições, gerar documentação, assinar, enviar à SADAIC e tramitar na DNDA, primeiro é preciso:",
    }),
    tenantSetupCreatePublisher: langGetter({
      es: "Crear Editorial",
      en: "Create Publisher",
      pt: "Criar Editora",
    }),
    tenantSetupEditProfile: langGetter({
      es: "Editar Datos del Cliente",
      en: "Edit Client Data",
      pt: "Editar Dados do Cliente",
    }),
    tenantSetupItemOwnPublisher: langGetter({
      es: "Editorial propia: la primera que se crea en el menú Editoriales queda establecida como propia",
      en: "Own publisher: the first one created in the Publishers menu becomes your own publisher",
      pt: "Editora própria: a primeira criada no menu Editoras fica definida como própria",
    }),
    tenantSetupItemSigner: langGetter({
      es: "Un representante firmante con email en la editorial propia",
      en: "A signing representative with email in your own publisher",
      pt: "Um representante signatário com email na editora própria",
    }),
    tenantSetupItemFtp: langGetter({
      es: "Credenciales del FTP de SADAIC (URL, usuario y contraseña) en Datos del Cliente",
      en: "SADAIC FTP credentials (URL, user and password) in Client Data",
      pt: "Credenciais do FTP da SADAIC (URL, usuário e senha) em Dados do Cliente",
    }),
    tenantSetupItemTad: langGetter({
      es: "Datos del gestor TAD/DNDA (documento y número de trámite) en Datos del Cliente",
      en: "TAD/DNDA manager data (ID and procedure number) in Client Data",
      pt: "Dados do gestor TAD/DNDA (documento e número de trâmite) em Dados do Cliente",
    }),
    // ── Revisión de campos del contrato (visor de la edición firmada) ──
    contractFieldsReviewButton: langGetter({
      es: "Revisar campos",
      en: "Review fields",
      pt: "Revisar campos",
    }),
    contractFieldsDialogTitle: langGetter({
      es: "Revisión de campos",
      en: "Field review",
      pt: "Revisão de campos",
    }),
    contractFieldsHint: langGetter({
      es: "Un clic en un campo lo ubica en el documento. Si aparece más de una vez, cada clic va a la siguiente.",
      en: "A click on a field finds it in the document. If it appears more than once, each click goes to the next one.",
      pt: "Um clique em um campo o localiza no documento. Se aparecer mais de uma vez, cada clique vai para a próxima.",
    }),
    contractFieldsNoMapNotice: langGetter({
      es: "Este documento se generó antes de la revisión de campos",
      en: "This document was generated before field review was available",
      pt: "Este documento foi gerado antes da revisão de campos",
    }),
    contractFieldsLoadError: langGetter({
      es: "No se pudo abrir el documento",
      en: "The document could not be opened",
      pt: "Não foi possível abrir o documento",
    }),
    contractFieldsReloadPage: langGetter({
      es: "Hay una versión nueva de la aplicación: para abrir el documento hay que recargar la página",
      en: "A new version of the app is available: the page needs to be reloaded to open the document",
      pt: "Há uma nova versão do aplicativo: é preciso recarregar a página para abrir o documento",
    }),
    contractFieldsReloadButton: langGetter({
      es: "Recargar la página",
      en: "Reload the page",
      pt: "Recarregar a página",
    }),
    contractFieldsOccurrenceOf: langGetter({
      es: "{current} de {total}",
      en: "{current} of {total}",
      pt: "{current} de {total}",
    }),
    contractFieldsGroupPublishers: langGetter({
      es: "Editoriales",
      en: "Publishers",
      pt: "Editoras",
    }),
    contractFieldsGroupAuthors: langGetter({
      es: "Autores",
      en: "Authors",
      pt: "Autores",
    }),
    contractFieldsGroupWorks: langGetter({
      es: "Obras",
      en: "Works",
      pt: "Obras",
    }),
    contractFieldsGroupTerritories: langGetter({
      es: "Territorios",
      en: "Territories",
      pt: "Territórios",
    }),
    contractFieldsGroupConditions: langGetter({
      es: "Condiciones",
      en: "Terms",
      pt: "Condições",
    }),
    contractFieldsGroupEditorial: langGetter({
      es: "Editorial",
      en: "Publisher",
      pt: "Editora",
    }),
    contractFieldsGroupEdition: langGetter({
      es: "Edición",
      en: "Edition",
      pt: "Edição",
    }),
    contractFieldsGroupSigners: langGetter({
      es: "Firmantes",
      en: "Signers",
      pt: "Signatários",
    }),
    contractFieldsGroupDerived: langGetter({
      es: "Calculados",
      en: "Calculated",
      pt: "Calculados",
    }),
    contractFieldsGroupOther: langGetter({
      es: "Otros",
      en: "Other",
      pt: "Outros",
    }),

    // ── KAN-448: la letra al generar la documentación ──
    editionLyricsWithoutLyricists: langGetter({
      es: "{title}: tiene letra, pero ningún autor tiene porcentaje de letra. Asigná el porcentaje de letra a quien la escribió o borrá la letra.",
      en: "{title}: it has lyrics, but no author has a lyrics percentage. Assign the lyrics percentage to whoever wrote them or delete the lyrics.",
      pt: "{title}: tem letra, mas nenhum autor tem porcentagem de letra. Atribua a porcentagem de letra a quem a escreveu ou apague a letra.",
    }),
    editionLyricistsWithoutLyrics: langGetter({
      es: "{title}: falta la letra. Hay autores con porcentaje de letra: cargá la letra en «{field}» o revisá esos porcentajes.",
      en: "{title}: the lyrics are missing. Some authors have a lyrics percentage: enter the lyrics in “{field}” or review those percentages.",
      pt: "{title}: falta a letra. Há autores com porcentagem de letra: preencha a letra em “{field}” ou revise essas porcentagens.",
    }),

    // ── KAN-450: los datos de los autores al generar la documentación ──
    editionAuthorMissingOne: langGetter({
      es: "{author}: falta {fields}.",
      en: "{author}: missing {fields}.",
      pt: "{author}: falta {fields}.",
    }),
    editionAuthorMissingMany: langGetter({
      es: "{author}: faltan {fields}.",
      en: "{author}: missing {fields}.",
      pt: "{author}: faltam {fields}.",
    }),

    // ── KAN-450: datos validados con DNI y advertencias del autor ──
    authorDniValidatedField: langGetter({
      es: "Dato validado con DNI",
      en: "Verified with ID",
      pt: "Dado validado com documento",
    }),
    authorDniValidatedRecord: langGetter({
      es: "Datos validados con DNI",
      en: "Data verified with ID",
      pt: "Dados validados com documento",
    }),
    authorIncompleteWarning: langGetter({
      es: "Faltan datos para incluir al autor en una documentación: {fields}.",
      en: "Missing data required to include this author in documentation: {fields}.",
      pt: "Faltam dados para incluir o autor em uma documentação: {fields}.",
    }),
    authorWithoutIpiOrDniWarning: langGetter({
      es: "No tiene IPI y SADAIC todavía no recibió su DNI: no puede incluirse en una documentación hasta que tenga uno de los dos.",
      en: "This author has no IPI and SADAIC has not received their ID yet: they cannot be included in documentation until one of the two is available.",
      pt: "Não tem IPI e a SADAIC ainda não recebeu o documento: não é possível incluir o autor em uma documentação até que tenha um dos dois.",
    }),
    authorStatusColumn: langGetter({
      es: "Validación",
      en: "Validation",
      pt: "Validação",
    }),

    // ── KAN-450: solicitud de validación al guardar el alta ──
    authorValidationOfferTitle: langGetter({
      es: "Alta guardada",
      en: "Author saved",
      pt: "Cadastro salvo",
    }),
    authorValidationOfferBody: langGetter({
      es: "¿Enviar ahora a {name} la solicitud de validación de datos? Le llegará a {email}.",
      en: "Send {name} the data validation request now? It will go to {email}.",
      pt: "Enviar agora a {name} a solicitação de validação de dados? Ela chegará em {email}.",
    }),
    authorValidationOfferSend: langGetter({
      es: "Enviar solicitud de validación",
      en: "Send validation request",
      pt: "Enviar solicitação de validação",
    }),
    authorValidationOfferLater: langGetter({
      es: "Ahora no",
      en: "Not now",
      pt: "Agora não",
    }),
    authorValidationOfferSent: langGetter({
      es: "Solicitud enviada a {email}.",
      en: "Request sent to {email}.",
      pt: "Solicitação enviada para {email}.",
    }),
    // Los datos de este autor ya están confirmados (`already_validated`): se nombra el ícono que pide
    // una validación nueva, el de datos junto al Email, con su tooltip cuando ya hubo una.
    authorValidationOfferAlreadyValidated: conIcono("emailResendValidateData", {
      es: (icono) =>
        `Los datos de este autor ya están confirmados. Para pedirle que los vuelva a validar, usá el ícono «${icono}» junto al Email, en el formulario del autor.`,
      en: (icono) =>
        `This author's data is already confirmed. To ask them to validate it again, use the “${icono}” icon next to the E-mail, in the author's form.`,
      pt: (icono) =>
        `Os dados deste autor já estão confirmados. Para pedir que os valide de novo, use o ícone «${icono}» ao lado do E-mail, no formulário do autor.`,
    }),
    // `already_verified`, de un server anterior a `scope`: el registro de verificación es uno por email
    // y empresa, y el email pudo verificarse por otro registro (el contacto de una editorial), no por
    // este autor. No dice que el autor confirmó sus datos; se nombra el ícono de datos junto al Email,
    // con su tooltip cuando todavía no hubo una validación.
    authorValidationOfferAlreadyVerified: conIcono("emailValidateData", {
      es: (icono) =>
        `Ese email ya está verificado. Para pedirle al autor que valide sus datos con su DNI, usá el ícono «${icono}» junto al Email, en el formulario del autor.`,
      en: (icono) =>
        `That email is already verified. To ask the author to validate their data with their ID, use the “${icono}” icon next to the E-mail, in the author's form.`,
      pt: (icono) =>
        `Esse e-mail já está verificado. Para pedir ao autor que valide seus dados com o documento, use o ícone «${icono}» ao lado do E-mail, no formulário do autor.`,
    }),
    // La demora es por email y empresa, no por autor: el envío reciente pudo ser para otro registro
    // con el mismo email. Por eso se habla del email.
    authorValidationOfferRateLimited: langGetter({
      es: "Ya se envió una solicitud a ese email hace poco. Vas a poder reenviarla en unos minutos desde el formulario del autor.",
      en: "A request was sent to that email recently. You can resend it in a few minutes from the author's form.",
      pt: "Uma solicitação foi enviada a esse e-mail há pouco. Poderá reenviá-la em alguns minutos pelo formulário do autor.",
    }),
    // Junto al Email hay dos íconos: se nombra el de validar email y datos, con su tooltip cuando
    // todavía no hubo una validación (la solicitud no salió).
    authorValidationOfferFailed: conIcono("emailValidateData", {
      es: (icono) =>
        `No se pudo enviar la solicitud. Se puede enviar desde el formulario del autor, con el ícono «${icono}» junto al Email.`,
      en: (icono) =>
        `The request could not be sent. It can be sent from the author's form, with the “${icono}” icon next to the E-mail.`,
      pt: (icono) =>
        `Não foi possível enviar a solicitação. Ela pode ser enviada pelo formulário do autor, com o ícone «${icono}» ao lado do E-mail.`,
    }),
    // No llegó una respuesta de la app que se pueda leer (plazo vencido, red caída, la respuesta de un
    // proxy o un cuerpo que no es JSON): el server graba el envío y encola el mail antes de contestar,
    // así que la solicitud pudo haber salido. Reenviarla sin mirar le mandaría al autor un segundo mail.
    // El texto no habla de tiempo: también vale para una caída de red. Junto al Email hay dos acciones
    // (verificar solo el email, validar email y datos): se nombran las dos, con sus tooltips.
    authorValidationOfferNoResponse: {
      get(this: { language: LanguageCodes; emailVerifyOnly: string; emailValidateData: string }) {
        const verificar = this.emailVerifyOnly;
        const validar = this.emailValidateData;
        return {
          es: `No llegó la respuesta del servidor: puede que la solicitud se haya enviado. Antes de volver a enviarla con «${verificar}» o «${validar}», revisá el estado junto al Email, en el formulario del autor.`,
          en: `The server's response did not arrive: the request may have been sent. Before sending it again with “${verificar}” or “${validar}”, check the status next to the E-mail, in the author's form.`,
          pt: `A resposta do servidor não chegou: a solicitação pode ter sido enviada. Antes de enviá-la novamente com «${verificar}» ou «${validar}», verifique o estado ao lado do E-mail, no formulário do autor.`,
        }[this.language];
      },
      enumerable: true,
      configurable: true,
    } as PropertyDescriptor,
  };
}

export function customErrorMessages(): Record<
  string,
  { [key: string]: string }
> {
  return {
    USER_PROFILE_INCOMPLETE: {
      es: "Tu perfil está incompleto. Faltan estos campos:",
      en: "Your profile is incomplete. These fields are missing:",
      pt: "Seu perfil está incompleto. Faltam estes campos:",
    },
    ENTITY_LOCKED_BY_EDITION_DOCUMENTATION: {
      es: "Modificación bloqueada por documentación en circulación",
      en: "Modification blocked by documentation in flight",
      pt: "Modificação bloqueada por documentação em circulação",
    },
    PLACEHOLDER_GENRE_IMMUTABLE: {
      es: "El género provisorio no se puede cambiar",
      en: "The placeholder genre cannot be changed",
      pt: "O gênero provisório não pode ser alterado",
    },
    EDITION_LYRICS_INCONSISTENT: {
      es: "La letra de algunas obras no coincide con el porcentaje de letra de sus autores. No se puede generar la documentación hasta corregirlo:",
      en: "The lyrics of some works do not match their authors' lyrics percentages. The documentation cannot be generated until this is corrected:",
      pt: "A letra de algumas obras não coincide com a porcentagem de letra de seus autores. Não é possível gerar a documentação até corrigir isso:",
    },
    EDITION_IDENTIFIERS_INVALID: {
      es: "Hay identificadores (ISRC, ISWC, IPI o CAE/IPI) que no cumplen las reglas. No se puede continuar hasta corregirlos:",
      en: "Some identifiers (ISRC, ISWC, IPI or CAE/IPI) do not meet the rules. This cannot continue until they are corrected:",
      pt: "Há identificadores (ISRC, ISWC, IPI ou CAE/IPI) que não cumprem as regras. Não é possível continuar até corrigi-los:",
    },
    IDENTIFIER_INVALID: {
      es: "Identificador inválido",
      en: "Invalid identifier",
      pt: "Identificador inválido",
    },
    PUBLISHER_REQUIRES_SIGNER: {
      es: "No se puede guardar la editorial sin al menos un representante firmante.",
      en: "The publisher cannot be saved without at least one signing representative.",
      pt: "Não é possível salvar a editora sem ao menos um representante signatário.",
    },
    PUBLISHER_SAME_CUIT_AS_AUTHOR: {
      es: "No se puede asociar una editorial persona física con el mismo CUIT/CUIL que el autor: un editor persona física no puede publicar sus propias obras.",
      en: "Cannot associate a natural-person publisher with the same Tax ID as the author: a natural-person publisher cannot publish their own works.",
      pt: "Não é possível associar uma editora pessoa física com o mesmo CUIT/CUIL que o autor: uma editora pessoa física não pode publicar suas próprias obras.",
    },
    TENANT_PROFILE_INCOMPLETE: {
      es: "Faltan datos de la empresa o persona titular para registrar la obra. Comunicate con el Departamento de Gestión de TAD:",
      en: "Missing data for the owner entity to register the work. Contact the TAD Management Department:",
      pt: "Faltam dados da empresa ou pessoa titular para registrar a obra. Entre em contato com o Departamento de Gestão de TAD:",
    },
    NO_AVAILABLE_PAYMENTS: {
      es: "Falta de comprobantes",
      en: "Missing payment receipts",
      pt: "Falta de comprovantes",
    },
    AUTHOR_MISSING_EMAIL: {
      es: "Estos autores no tienen email; sin él no se pueden generar los documentos:",
      en: "These authors have no email; documents cannot be generated without it:",
      pt: "Estes autores não têm email; sem ele não é possível gerar os documentos:",
    },
    AUTHOR_MISSING_ANSES_CERTIFICATE: {
      es: "Los siguientes autores argentinos no tienen la constancia de CUIL de ANSES. No se puede generar documentación hasta que la obtengan; si ANSES no validó sus datos, revisá y corregí sus datos personales:",
      en: "The following Argentine authors are missing their ANSES CUIL certificate. Documentation cannot be generated until it is obtained; if ANSES did not validate their data, review and correct their personal details:",
      pt: "Os seguintes autores argentinos não têm a constância de CUIL da ANSES. Não é possível gerar documentação até obtê-la; se a ANSES não validou seus dados, revise e corrija seus dados pessoais:",
    },
    NO_PUBLISHER_SIGNERS: {
      es: "Estas editoriales no tienen representantes designados como firmantes; cada una necesita al menos uno:",
      en: "These publishers have no representatives designated as signers; each one needs at least one:",
      pt: "Estas editoras não têm representantes designados como signatários; cada uma precisa de pelo menos um:",
    },
    SIGNER_MISSING_EMAIL: {
      es: "Estos representantes firmantes no tienen email; cada firmante necesita uno:",
      en: "These signer representatives have no email; each signer needs one:",
      pt: "Estes representantes signatários não têm email; cada signatário precisa de um:",
    },
    SIGNERS_CHANGED_SINCE_GENERATION: {
      es: "Los firmantes cambiaron desde la última generación de documentos. Volvé a generar los documentos antes de enviar a firmar:",
      en: "Signers have changed since the last document generation. Regenerate documents before sending for signature:",
      pt: "Os signatários mudaram desde a última geração de documentos. Regenere os documentos antes de enviar para assinatura:",
    },
    WORK_PERCENTAGE_EXCEEDS_100: {
      es: "La suma total de porcentajes de letra y música no puede superar el 100%",
      en: "The total sum of lyrics and music percentages cannot exceed 100%",
      pt: "A soma total de porcentagens de letra e música não pode exceder 100%",
    },
    VALIDATION_WORK_AUTHORS: {
      es: "Error en datos de autores/compositores",
      en: "Error in authors/composers data",
      pt: "Erro nos dados de autores/compositores",
    },
    WORK_AUTHOR_PERCENTAGES_BOTH_ZERO: {
      es: "Un autor no puede tener 0% de letra y 0% de música simultáneamente: debe participar al menos en una",
      en: "An author cannot have 0% lyrics and 0% music at the same time: must participate in at least one",
      pt: "Um autor não pode ter 0% de letra e 0% de música ao mesmo tempo: deve participar de pelo menos uma",
    },
    MUSICAL_WORK_MISSING_SCORE: {
      es: "Las siguientes obras musicales no tienen partitura cargada",
      en: "The following musical works do not have a score uploaded",
      pt: "As seguintes obras musicais não têm partitura carregada",
    },
    EDITION_MISSING_PUBLICATION_DATE: {
      es: "La edición no tiene fecha de publicación",
      en: "The edition does not have a publication date",
      pt: "A edição não tem data de publicação",
    },
    PUBLISHER_NATURAL_PERSON_INCOMPLETE: {
      es: "Hay editoriales Persona Física a las que les faltan datos que requiere la DNDA:",
      en: "Some Natural Person publishers are missing data required by DNDA:",
      pt: "Algumas editoras Pessoa Física estão com dados faltantes requeridos pela DNDA:",
    },
    EDITION_AUTHORS_INCOMPLETE: {
      es: "A algunos autores les faltan datos personales y no se puede continuar hasta completarlos. Cargalos en el formulario de cada autor o enviale la validación para que los complete:",
      en: "Some authors are missing personal data and you cannot continue until it is completed. Enter it in each author's form or send the author the validation so they can complete it themselves:",
      pt: "Alguns autores estão sem dados pessoais e não é possível continuar até completá-los. Preencha-os no formulário de cada autor ou envie-lhe a validação para que os complete:",
    },
    EDITION_AUTHORS_WITHOUT_IPI_OR_DNI: {
      es: "Estos autores no tienen IPI y SADAIC todavía no recibió su DNI: sin uno de los dos, SADAIC no puede registrar las obras. Cargá el IPI o enviale (o reenviale) la validación a cada autor para que mande la foto de su DNI:",
      en: "These authors have no IPI and SADAIC has not received their ID yet: without one of the two, SADAIC cannot register the works. Enter the IPI or send (or resend) the validation to each author so they can send the photo of their ID:",
      pt: "Estes autores não têm IPI e a SADAIC ainda não recebeu o documento deles: sem um dos dois, a SADAIC não pode registrar as obras. Informe o IPI ou envie (ou reenvie) a validação a cada autor para que mande a foto do documento:",
    },
    AUTHOR_DATA_INCOMPLETE: {
      es: "Al autor le faltan datos para pedir la constancia de CUIL. Cargalos en su formulario o enviale la validación para que los complete:",
      en: "The author is missing data needed to request the CUIL certificate. Enter it in the author's form or send the author the validation so they can complete it:",
      pt: "Faltam dados do autor para pedir a constância de CUIL. Preencha-os no formulário do autor ou envie-lhe a validação para que os complete:",
    },
    // El dato puede ser el número (taxId), mal formado, o el tipo de documento (taxIdType), que no es
    // CUIT/CUIL ni DU: el título vale para los dos, y el renglón de cada autor lleva la etiqueta del dato
    // (customErrorProcessors).
    AUTHOR_TAX_ID_INVALID: {
      es: "Un dato del documento de este autor no sirve para pedir la constancia de CUIL. Revisalo en su formulario:",
      en: "Some of this author's ID information cannot be used to request the CUIL certificate. Please review it in the author's form:",
      pt: "Um dado do documento deste autor não serve para pedir a constância de CUIL. Revise-o no formulário do autor:",
    },
    EDITION_CONTENT_REQUIREMENTS_NOT_MET: {
      es: "Falta contenido que la edición necesita para generar la documentación:",
      en: "Some content the edition needs to generate the documentation is missing:",
      pt: "Falta conteúdo de que a edição precisa para gerar a documentação:",
    },
    // Hotfix de la capa segura: el server valida el ticket de la subida, el nombre del archivo
    // (un solo tramo de ruta) y su identificador antes de tocar el disco o el storage; un archivo
    // guardado antes con un nombre o un identificador así ya no se descarga ni se borra.
    INVALID_UPLOAD_FILENAME: {
      es: "El nombre del archivo no es válido: no puede llevar «/» ni «\\», ni ser «.» ni «..». Cambiale el nombre y volvé a adjuntarlo.",
      en: "The file name is not valid: it cannot contain “/” or “\\”, or be “.” or “..”. Rename the file and attach it again.",
      pt: "O nome do arquivo não é válido: não pode conter «/» nem «\\», nem ser «.» ou «..». Renomeie o arquivo e anexe-o novamente.",
    },
    INVALID_UPLOAD_UUID: {
      es: "El identificador del archivo no es válido. Volvé a adjuntar el archivo y guardá nuevamente.",
      en: "The file identifier is not valid. Attach the file again and save.",
      pt: "O identificador do arquivo não é válido. Anexe o arquivo novamente e salve.",
    },
    INVALID_UPLOAD_TICKET: {
      es: "La subida del archivo no es válida. Volvé a adjuntarlo y guardá nuevamente.",
      en: "The file upload is not valid. Attach the file again and save.",
      pt: "O envio do arquivo não é válido. Anexe o arquivo novamente e salve.",
    },
    // Rol único por usuario: el server rechaza un alta activa sin rol, y una edición que activa a un
    // usuario sin rol o que le quita el rol a un activo (los demás datos de un activo sin rol se
    // siguen editando).
    ACTIVE_USER_NEEDS_ROLE: {
      es: "Un usuario activo tiene que tener un rol. Elegile uno, o dejalo inactivo, y guardá nuevamente.",
      en: "An active user must have a role. Choose one, or leave the user inactive, and save again.",
      pt: "Um usuário ativo precisa ter um papel. Escolha um, ou deixe o usuário inativo, e salve novamente.",
    },
  };
}
